import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Paperclip, Loader2, X, Smile, Send } from 'lucide-react';
import { toast } from 'sonner';
import { messagingAPI } from '@/services/api';

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const MessageInput = ({ onSendMessage, onTypingChange, conversationId, disabled = false }) => {
  const [message, setMessage] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const textareaRef = useRef(null);
  const composerRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const fileInputRef = useRef(null);
  const typingTimerRef = useRef(null);
  const typingActiveRef = useRef(false);

  useEffect(() => {
    const input = textareaRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(120, Math.max(42, input.scrollHeight))}px`;
  }, [message]);

  useEffect(() => {
    if (!showEmoji) return undefined;
    const dismissOutside = (event) => {
      if (!composerRef.current?.contains(event.target)) setShowEmoji(false);
    };
    const dismissEscape = (event) => {
      if (event.key === 'Escape') {
        setShowEmoji(false);
        textareaRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', dismissOutside);
    document.addEventListener('keydown', dismissEscape);
    return () => {
      document.removeEventListener('pointerdown', dismissOutside);
      document.removeEventListener('keydown', dismissEscape);
    };
  }, [showEmoji]);

  const stopTyping = useCallback(() => {
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = null;
    if (typingActiveRef.current) {
      typingActiveRef.current = false;
      setIsTyping(false);
      onTypingChange?.(false);
    }
  }, [onTypingChange]);

  useEffect(() => () => stopTyping(), [conversationId, stopTyping]);

  const handleSend = async () => {
    if (pendingFile) {
      await handleSendFile();
      return;
    }
    if (message.trim()) {
      const content = message.trim();
      setMessage('');
      stopTyping();
      try {
        await onSendMessage(content, 'text');
      } catch (err) {
        toast.error(err.message || 'Message failed to send. Retry when ready.');
      }
    }
  };

  const handleSendFile = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const { upload_url, file_url, method, upload_fields = {}, storage_key, temporary } = await messagingAPI.getUploadUrl(
        pendingFile.name,
        pendingFile.size,
        conversationId,
      );

      if (temporary) toast.warning('Attachments in this test environment expire after 15 minutes.');
      if (method === 'GCS_POST') {
        const formData = new FormData();
        Object.entries(upload_fields).forEach(([key, value]) => formData.append(key, value));
        formData.append('file', pendingFile);
        const uploadRes = await fetch(upload_url, { method: 'POST', body: formData });
        if (!uploadRes.ok) throw new Error('Upload failed');
      } else {
        const formData = new FormData();
        formData.append('file', pendingFile);
        const uploadRes = await fetch(upload_url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('proovam_token')}`,
          },
          body: formData,
        });
        if (!uploadRes.ok) throw new Error('Upload failed');
      }

      const fileType = pendingFile.type?.startsWith('image/') ? 'image' : 'file';
      await onSendMessage(file_url, fileType, {
        caption: message.trim(),
        file_name: pendingFile.name,
        file_size: pendingFile.size,
        file_storage_key: storage_key,
      });
      setPendingFile(null);
      setMessage('');
    } catch (err) {
      toast.error(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File must be under 10MB');
      return;
    }
    setPendingFile(file);
    e.target.value = '';
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e) => {
    const next = e.target.value;
    setMessage(next);
    if (!next.trim()) {
      stopTyping();
      return;
    }
    if (!isTyping) {
      setIsTyping(true);
      typingActiveRef.current = true;
      typingTimerRef.current = setTimeout(() => {
        typingTimerRef.current = null;
        onTypingChange?.(true);
      }, 300);
    }
  };

  return (
    <div ref={composerRef} className="message-composer">
      {showEmoji && <div className="chat-emoji" role="group" aria-label="Emoji picker">{["😀", "😊", "🎉", "👍", "❤️", "👏", "🚀", "✅", "🙏", "💡", "👋", "🤝"].map((emoji) => <button type="button" key={emoji} aria-label={`Insert ${emoji}`} onClick={() => { setMessage((current) => current + emoji); setShowEmoji(false); textareaRef.current?.focus(); }}>{emoji}</button>)}</div>}
      {pendingFile && (
        <div className="mb-2 flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm">
          <Paperclip className="h-4 w-4 text-gray-500 shrink-0" />
          <span className="flex-1 truncate text-gray-700">{pendingFile.name}</span>
          <span className="text-xs text-gray-400">
            {(pendingFile.size / 1024).toFixed(0)} KB
          </span>
          <button
            type="button"
            onClick={() => setPendingFile(null)}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Remove attachment"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="composer-row">
        <button type="button" className="composer-icon" aria-label="Choose emoji" aria-expanded={showEmoji} disabled={disabled || uploading} onClick={() => setShowEmoji(!showEmoji)}><Smile className="h-5 w-5"/></button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt,.md,.csv,.mp4,.mov"
          onChange={handleFileSelect}
          data-testid="message-file-input"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
          className="composer-icon"
          aria-label="Attach file"
          data-testid="message-attach-btn"
        >
          <Paperclip className="h-5 w-5" />
        </button>
        <textarea
          ref={textareaRef}
          aria-label="Message"
          value={message}
          onChange={handleChange}
          onBlur={stopTyping}
          onKeyDown={handleKeyPress}
          placeholder={pendingFile ? 'Add a caption (optional)...' : 'Type a message...'}
          disabled={disabled || uploading}
          rows="1"
          className="composer-text"
        />
        <button
          type="button"
          aria-label="Send message"
          onClick={handleSend}
          disabled={disabled || uploading || (!message.trim() && !pendingFile)}
          className="composer-send"
          data-testid="message-send-btn"
        >
          {uploading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="h-5 w-5" />
          )}
        </button>
      </div>
      <p className="composer-hint">
        Press Enter to send · Attach files up to 10MB
      </p>
    </div>
  );
};

export default MessageInput;
